import { useTranslation } from 'react-i18next';
import { DropdownSearch, Dropdown, GroupedExpandableList, useSnackbar } from 'component-library';
import { type ListDropdownItem } from '@sr/types';
import { useContainerGroups } from '#lib/hooks/useContainerGroups';

export const ContainerPicker = ({
  onChange,
  containerType,
  locationDwKey,
  useGrandParentLocation,
  errorMessage,
}: {
  onChange: (type: string, name: string) => void;
  containerType: string | undefined;
  locationDwKey: string;
  useGrandParentLocation?: boolean;
  errorMessage?: string;
}) => {
  const { t } = useTranslation();

  const {
    containers: containerGroups,
    isPending,
    error,
  } = useContainerGroups({
    selectedBusinessPartner: locationDwKey,
    useGrandParentLocation,
  });

  const { showSnackbar } = useSnackbar();

  const dropdownItems: ListDropdownItem[] = containerGroups.map((container) => ({
    label: container.groupName,
    type: 'group',
    value: container.groupName,
    children: container.containers.map((child) => ({
      label: child.containerTypeName,
      type: 'value',
      value: child.containerTypeId,
      children: [],
    })),
  }));

  dropdownItems.push({
    label: t('rented-containers.add-containers.step1.add-container.modal.container.group.other'),
    type: 'group',
    value: 'other',
    children: [
      {
        label: t(
          'rented-containers.add-containers.step1.add-container.modal.container.group.other_1',
        ),
        type: 'group',
        value: 'other_1',
        children: [],
      },
      {
        label: t(
          'rented-containers.add-containers.step1.add-container.modal.container.group.other_2',
        ),
        type: 'group',
        value: 'other_2',
        children: [],
      },
    ],
  });

  const handleContainerNameChange = (value: string) => {
    const containerName =
      dropdownItems.flatMap((group) => group.children).find((c) => c.value === value)?.label || '';

    return containerName;
  };

  if (error) {
    if (error.message.includes('Timeout')) {
      showSnackbar({
        severity: 'error',
        description: t('add-container.container-picker.components.message.error.timeout.text'),
      });
    } else {
      showSnackbar({
        severity: 'error',
        description: t('message.components.message.error.default.text'),
      });
    }
  }

  return (
    <Dropdown
      loading={isPending}
      error={!!errorMessage}
      errorMessage={errorMessage}
      items={dropdownItems}
      searchPlaceholder={t(
        'rented-containers.add-containers.step1.add-container.modal.container.search.placeholder',
      )}
      selectedValues={containerType ? [containerType] : []}
      onChange={(value) => {
        onChange(value[0], handleContainerNameChange(value[0]));
      }}
      label={t('rented-containers.add-containers.step1.add-container.modal.container.title')}
      placeholder={t(
        'rented-containers.add-containers.step1.add-container.modal.container.placeholder',
      )}
    >
      <DropdownSearch />
      <GroupedExpandableList />
    </Dropdown>
  );
};
